// TFVF Practice Lab — trainee picks a scenario, sees the caller brief
// + consultant objective + assessment focus, then starts an attempt.
// After the role-play they submit a self-reflection and self-score.
// Suggested-response text is deliberately NOT surfaced pre-attempt
// (per Phase 2 spec: don't show the trainee every prospect response
// before the role-play begins).
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, PlayCircle, ArrowLeft, Target, MessageSquare, CheckCircle2, Sparkles,
} from 'lucide-react';
import { API, authHeaders, renderMarkdown } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfPractice = () => {
  const { token } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [reflection, setReflection] = useState('');
  const [selfScore, setSelfScore] = useState(3);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiFeedback, setAiFeedback] = useState('');

  const loadAttempts = useCallback(() => {
    axios.get(`${API}/tfvf/practice/attempts/mine`, { headers: authHeaders(token) })
      .then(r => setAttempts(r.data?.attempts || []));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      axios.get(`${API}/tfvf/practice/scenarios`, { headers: authHeaders(token) }),
      axios.get(`${API}/tfvf/practice/attempts/mine`, { headers: authHeaders(token) }),
    ]).then(([s, a]) => {
      setScenarios(s.data?.scenarios || []);
      setAttempts(a.data?.attempts || []);
    }).finally(() => setLoading(false));
  }, [token]);

  const openScenario = async (id) => {
    setActiveId(id);
    setAttempt(null);
    setDetail(null);
    setReflection('');
    setSelfScore(3);
    const r = await axios.get(`${API}/tfvf/practice/scenarios/${id}`, { headers: authHeaders(token) });
    setDetail(r.data);
  };

  const startAttempt = async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/tfvf/practice/attempts`, { scenario_id: activeId }, { headers: authHeaders(token) });
      setAttempt(r.data);
      toast.success('Attempt started — take the role-play call, then submit your reflection.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start');
    } finally { setBusy(false); }
  };

  const submitAttempt = async () => {
    if (!attempt) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/tfvf/practice/attempts/${attempt.id}/submit`,
        { reflection, self_score: Number(selfScore) },
        { headers: authHeaders(token) },
      );
      toast.success('Practice attempt submitted');
      // Trigger AI feedback right after submit — graceful if disabled.
      setAiBusy(true);
      try {
        const r = await axios.post(
          `${API}/tfvf/practice/attempts/${attempt.id}/ai-feedback`,
          {},
          { headers: authHeaders(token) },
        );
        setAiFeedback(r.data?.feedback || '');
        if (!r.data?.feedback && r.data?.error) toast.info('AI feedback unavailable — reflection saved.');
      } catch { /* silent — AI is a bonus */ }
      finally { setAiBusy(false); }
      loadAttempts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;

  // ── Detail view ─────────────────────────────────────────
  if (activeId && detail) {
    return (
      <div className="space-y-5" data-testid="tfvf-practice-detail">
        <button onClick={() => { setActiveId(null); setDetail(null); }} className="text-xs text-[color:var(--tf-muted)] hover:text-[color:var(--tf-forest)] inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> All scenarios
        </button>

        <div className="tf-card rounded-xl p-6">
          <p className="tf-eyebrow">{detail.id} · {detail.sub_intent?.replace(/_/g, ' ')}</p>
          <h1 className="text-2xl font-bold text-[color:var(--tf-forest)] mt-0.5">{detail.title}</h1>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-l-4 pl-3" style={{ borderLeftColor: 'var(--tf-gold)' }}>
              <p className="tf-eyebrow flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Caller brief</p>
              <p className="text-sm text-[color:var(--tf-slate)] mt-1">{detail.caller_brief}</p>
            </div>
            <div className="border-l-4 pl-3" style={{ borderLeftColor: 'var(--tf-forest)' }}>
              <p className="tf-eyebrow flex items-center gap-1"><Target className="w-3 h-3" /> Your objective</p>
              <p className="text-sm text-[color:var(--tf-slate)] mt-1">{detail.consultant_objective}</p>
            </div>
          </div>

          {detail.assessment_focus?.length > 0 && (
            <div className="mt-4">
              <p className="tf-eyebrow">Assessment focus</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {detail.assessment_focus.map(f => (
                  <span key={f} className="tf-badge-forest text-xs px-2 py-0.5 rounded-full">{f}</span>
                ))}
              </div>
            </div>
          )}

          {!attempt ? (
            <button onClick={startAttempt} disabled={busy} className="tf-btn-primary rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 mt-5 disabled:opacity-50" data-testid="tfvf-practice-start">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              Start attempt
            </button>
          ) : (
            <div className="mt-5 p-4 rounded-lg border-2" style={{ borderColor: 'var(--tf-gold)', background: 'rgba(200,151,74,0.05)' }} data-testid="tfvf-practice-attempt-form">
              <p className="tf-eyebrow">Attempt {attempt.id.slice(0,8)} · in progress</p>
              <p className="text-xs text-[color:var(--tf-muted)] mt-1">
                Take the role-play call now (partner, manager or self-recording). When done, capture what you did well, what to change next time, and your own rating.
              </p>
              <label className="block text-xs font-semibold text-[color:var(--tf-forest)] mt-3">Reflection</label>
              <textarea
                value={reflection}
                onChange={e => setReflection(e.target.value)}
                rows={5}
                className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white"
                placeholder="I opened well and asked the goal question, but I answered price too fast. Next time I'll ask what stopped them last time before pricing…"
                data-testid="tfvf-practice-reflection"
              />
              <div className="flex items-center gap-3 mt-3">
                <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Self-score (1–5)</label>
                <input
                  type="number" min={1} max={5} value={selfScore}
                  onChange={e => setSelfScore(e.target.value)}
                  className="w-16 border border-[color:var(--tf-forest)]/15 rounded-lg px-2 py-1 text-sm bg-white"
                  data-testid="tfvf-practice-self-score"
                />
              </div>
              <button
                onClick={submitAttempt}
                disabled={busy || !reflection.trim()}
                className="tf-btn-gold rounded-lg px-4 py-2 text-sm font-semibold mt-4 disabled:opacity-50"
                data-testid="tfvf-practice-submit"
              >
                Submit reflection
              </button>

              {(aiBusy || aiFeedback) && (
                <div className="mt-4 rounded-lg border p-4" style={{ borderColor: 'var(--tf-forest)', background: 'rgba(26,61,46,0.04)' }} data-testid="tfvf-practice-ai-feedback">
                  <p className="tf-eyebrow flex items-center gap-2">
                    <Sparkles className="w-3 h-3" style={{ color: 'var(--tf-gold)' }} /> AI coaching notes
                  </p>
                  {aiBusy ? (
                    <p className="text-xs text-[color:var(--tf-muted)] mt-2 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Generating feedback…
                    </p>
                  ) : (
                    <div className="text-sm mt-2">{renderMarkdown(aiFeedback)}</div>
                  )}
                  {aiFeedback && (
                    <button
                      onClick={() => { setAttempt(null); setDetail(null); setActiveId(null); setAiFeedback(''); }}
                      className="tf-btn-primary rounded-lg px-3 py-1.5 text-xs font-semibold mt-3"
                      data-testid="tfvf-practice-done"
                    >
                      Done — back to scenarios
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────
  return (
    <div className="space-y-5" data-testid="tfvf-practice-page">
      <div className="tf-card rounded-xl p-5">
        <p className="tf-eyebrow">Practice Lab</p>
        <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">10 approved role-play scenarios</h2>
        <p className="text-xs text-[color:var(--tf-muted)] mt-1">
          Pick a scenario. You get the caller brief and your objective — not the model responses. Take the call, then log a reflection.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {scenarios.map(s => {
          const my = attempts.find(a => a.scenario_id === s.id);
          return (
            <button
              key={s.id}
              onClick={() => openScenario(s.id)}
              className="tf-card rounded-lg p-4 text-left hover:shadow-md transition-shadow"
              data-testid={`tfvf-scenario-${s.id}`}
            >
              <div className="flex items-center justify-between">
                <p className="tf-eyebrow">{s.id}</p>
                {my && <span className="tf-badge-forest text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> {my.status}
                </span>}
              </div>
              <p className="text-base font-semibold text-[color:var(--tf-forest)] mt-1">{s.title}</p>
              <p className="text-xs text-[color:var(--tf-muted)] mt-1 line-clamp-2">{s.brief_teaser}…</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {(s.assessment_focus || []).slice(0, 3).map(f => (
                  <span key={f} className="tf-badge-gold text-[10px] px-2 py-0.5 rounded-full">{f}</span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {attempts.length > 0 && (
        <div className="tf-card rounded-xl p-5">
          <p className="tf-eyebrow mb-2">My attempts ({attempts.length})</p>
          <div className="space-y-1 text-sm">
            {attempts.slice(0, 8).map(a => (
              <div key={a.id} className="flex items-center gap-3 text-xs" data-testid={`tfvf-practice-attempt-${a.id}`}>
                <span className="text-[color:var(--tf-muted)]">{new Date(a.started_at).toLocaleDateString()}</span>
                <span className="font-mono text-[color:var(--tf-forest)]">{a.scenario_id}</span>
                <span className="tf-badge-forest px-2 py-0.5 rounded-full text-[10px]">{a.status}</span>
                {a.self_score != null && <span className="text-[color:var(--tf-muted)]">self {a.self_score}/5</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TfvfPractice;
